<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { Languages } from 'lucide-vue-next';
import { LOCALES, type Locale } from 'shared';
import { usePrefsStore } from '@/stores/prefs';

/**
 * EN/ES, in the nav bar.
 *
 * One button that swaps, rather than a two-segment EN|ES control: with exactly
 * two languages a segmented toggle spends twice the width to say the same
 * thing, and on a phone the bar is already carrying five destinations. The
 * icon says what the control is for and the label says which language is on.
 *
 * The accessible name spells out what pressing it does, because "EN" on its
 * own does not tell you whether it is the current state or the destination —
 * a distinction sighted users get from the icon and context, and screen
 * reader users would otherwise have to guess at.
 *
 * Writing through `prefs` rather than i18n directly matters: that is what
 * persists the choice to the device and to the account, so the language
 * survives a reload and follows the reader to their other devices.
 */
const { t } = useI18n();
const prefs = usePrefsStore();

const ENDONYM: Record<Locale, string> = { en: 'English', es: 'Español' };

/** With two locales this is "the other one"; with more it would cycle. */
const next = computed<Locale>(
  () => LOCALES.find((code) => code !== prefs.prefs.locale) ?? prefs.prefs.locale,
);
</script>

<template>
  <button
    type="button"
    class="flex shrink-0 flex-col items-center gap-1 px-2.5 py-2.5 font-display text-[0.8125rem] text-muted transition-colors hover:text-parchment sm:flex-row sm:gap-2 sm:px-5 sm:text-sm"
    :aria-label="t('settings.switchLanguage', { language: ENDONYM[next] })"
    @click="prefs.setLocale(next)"
  >
    <Languages class="size-5" aria-hidden="true" />
    <span class="uppercase">{{ prefs.prefs.locale }}</span>
  </button>
</template>
